<template>
  <div class="HeroMedia">
    <!-- Video -->
    <mux-player
      v-if="media.video?.video?.muxPlaybackId"
      ref="desktopPlayer"
      :class="$b('video', {
        desktop: true,
        mobile: !media.mobileVideo?.video?.muxPlaybackId
      })"
      stream-type="on-demand"
      :playback-id="media.video.video.muxPlaybackId"
      :placeholder="`data:image/jpeg;base64, ${media.thumbhash}`"
      :thumbnail-time="0"
      :title="media.title"
      :accent-color="resolvedConfig.theme.colors.black"
      :primary-color="resolvedConfig.theme.colors.white"
      muted
      autoplay
      loop
      preload="none"
    />

    <mux-player
      v-if="media.mobileVideo?.video?.muxPlaybackId"
      ref="mobilePlayer"
      :class="$b('video', {
        mobile: true
      })"
      stream-type="on-demand"
      :playback-id="media.mobileVideo.video.muxPlaybackId"
      :placeholder="`data:image/jpeg;base64, ${media.thumbhash}`"
      :thumbnail-time="0"
      :title="media.title"
      :accent-color="resolvedConfig.theme.colors.black"
      :primary-color="resolvedConfig.theme.colors.white"
      muted
      autoplay
      loop
      preload="none"
    />

    <picture v-else>
      <source
        v-if="media.mobileImage && media.mobileImage.src"
        media="(orientation: portrait)"
        :sizes="imageSizes"
        :srcset="portraitSrcset"
      >

      <source
        :media="media.mobileImage && media.mobileImage.src ? '(orientation: landscape)' : null"
        :sizes="imageSizes"
        :srcset="landscapeSrcset"
      >

      <img
        class="HeroMedia__image"
        :src="media.image.src"
        :alt="media.image.alt"
        :sizes="imageSizes"
        :style="styles"
        :fetchpriority="isFirst ? 'high' : 'auto'"
      >
    </picture>
  </div>
</template>

<script>
import "@mux/mux-player"
import resolveConfig from 'tailwindcss/resolveConfig'
import tailwindConfig from '@/tailwind.config.js'

export default {
  name: 'HeroMedia',

  props: {
    media: {
      type: Object,
      required: true
    },
    isFirst: {
      type: Boolean,
      default: false
    }
  },

  setup() {
    const { imgixSize } = useImgix()
    return { imgixSize }
  },

  data () {
    return {
      videoKey: 0,
      imageSizes: '100vw',
      observer: null,
      resolvedConfig: resolveConfig(tailwindConfig)
    }
  },

  computed: {
    landscapeSrcset () {
      const { src, focalPoint } = this.media.image

      return `
        ${this.imgixSize(src, { size: 'landscape_4xl', focalPoint })} 2400w,
        ${this.imgixSize(src, { size: 'landscape_3xl', focalPoint })} 2000w,
        ${this.imgixSize(src, { size: 'landscape_2xl', focalPoint })} 1600w,
        ${this.imgixSize(src, { size: 'landscape_xl', focalPoint })} 1200w,
        ${this.imgixSize(src, { size: 'landscape_md', focalPoint })} 800w,
        ${this.imgixSize(src, { size: 'landscape_sm', focalPoint })} 600w,
      `
    },

    portraitSrcset () {
      if (!this.media.mobileImage) return null
      const { src, focalPoint } = this.media.mobileImage

      return `
        ${this.imgixSize(src, { size: 'portrait_xxl', focalPoint })} 1500w,
        ${this.imgixSize(src, { size: 'portrait_xl', focalPoint })} 1200w,
        ${this.imgixSize(src, { size: 'portrait_lg', focalPoint })} 800w,
        ${this.imgixSize(src, { size: 'portrait_md', focalPoint })} 600w,
        ${this.imgixSize(src, { size: 'portrait_sm', focalPoint })} 450w,
        ${this.imgixSize(src, { size: 'portrait_xs', focalPoint })} 320w
      `
    },

    styles () {
      if (!this.media.image.focalPoint) return

      const xPoint = `${this.media.image.focalPoint.x * 100}%`
      const yPoint = `${this.media.image.focalPoint.y * 100}%`

      return `object-position: ${xPoint} ${yPoint};`
    }
  },

  mounted() {
    // Add intersection observer to play video when in view
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.2,
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry?.target?.play()
        } else {
          entry?.target?.pause()
        }
      })
    }, options)

    if (this.$refs.desktopPlayer)
      this.observer.observe(this.$refs.desktopPlayer)
    
    if (this.$refs.mobilePlayer)
      this.observer.observe(this.$refs.mobilePlayer)
  },

  beforeUnmount() {
    this.observer?.disconnect()
  }
}
</script>

<style lang="postcss">
.HeroMedia,
.HeroMedia__video,
.HeroMedia__image {
  @apply w-full
    h-full;

  &--mobile {
    @apply block md:hidden;
  }

  &--desktop {
    @apply hidden md:block;
  }
}

.HeroMedia mux-player {
  --controls: none;
  --media-object-fit: cover;
  --media-object-position: center;
}

.HeroMedia__video,
.HeroMedia__image {
  @apply object-cover;
}
</style>
